import { camelCase } from 'lodash';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';
import { getTravelDocumentsValidators } from 'Components/widgets/contact-info/ContactInfoUtils';
import { t } from 'Src/i18n';
import { ENTER } from 'Utils/keyCodes';
export type CivilitiesType = ReadonlyArray<{
  label: string;
  value: string;
}>;
export const onKeyDownDataForm = (dataForm: string) => (e: KeyboardEvent) => {
  if (e.which === ENTER) {
    const inputs = document.querySelectorAll(
      `[data-form="${dataForm}"] input, [data-form="${dataForm}"] select`,
    );

    if (inputs) {
      // @ts-expect-error TODO(flow-migration) : flow does not reccognize id key in EventTarget
      const currentIndex = [...inputs].findIndex((i) => i.name === e.target.name);

      if (currentIndex < inputs.length - 1) {
        const selector = inputs[currentIndex + 1].name;
        const next = document.querySelector(`[name="${selector}"]`);

        if (next) {
          next.focus();
          scrollToElement(`[for="${selector}"]`, 100);
        }
      }
    }
  }
};
type DocumentVisibilityType = {
  isVisible: boolean;
  isRequired: boolean;
  label: string;
  type: string;
  validator: any;
};
export type DocumentFieldsVisibilitiesType = {
  idcardDocumentNumber: DocumentVisibilityType;
  idcardExpirationDate: DocumentVisibilityType;
  idcardIssuingDate: DocumentVisibilityType;
  idcardAuthority: DocumentVisibilityType;
  idcardBirthCountry: DocumentVisibilityType;
  idcardBirthCity: DocumentVisibilityType;
  idcardSecondName: DocumentVisibilityType;
  idcardThirdName: DocumentVisibilityType;
  idcardFourthName: DocumentVisibilityType;
  idcardNationality: DocumentVisibilityType;
  passportDocumentNumber: DocumentVisibilityType;
  passportExpirationDate: DocumentVisibilityType;
  passportIssuingDate: DocumentVisibilityType;
  passportAuthority: DocumentVisibilityType;
  passportBirthCountry: DocumentVisibilityType;
  passportBirthCity: DocumentVisibilityType;
  passportSecondName: DocumentVisibilityType;
  passportThirdName: DocumentVisibilityType;
  passportFourthName: DocumentVisibilityType;
  passportNationality: DocumentVisibilityType;
  identityNumber: DocumentVisibilityType;
};
type FieldVisibilityType = {
  isVisible: boolean;
  isRequired: boolean;
};
type FieldsVisibilitiesType = {
  civilityField: FieldVisibilityType;
  firstNameField: FieldVisibilityType;
  lastNameField: FieldVisibilityType;
  unicodeFirstNameField: FieldVisibilityType;
  unicodeLastNameField: FieldVisibilityType;
  emailField: FieldVisibilityType;
  birthdateField: FieldVisibilityType;
  gmNumberField: FieldVisibilityType;
  phonesField: FieldVisibilityType;
  sanitaryIdField: FieldVisibilityType;
  documentFieldsVisibilities: DocumentFieldsVisibilitiesType;
  shouldDisplayLegalInfo: boolean;
};
export const getFieldVisibilities = (
  attendeeSchema: Array<ValidationSchema>,
  locale: string,
  arrivalDate: any,
  departureDate: any,
): FieldsVisibilitiesType => {
  const checkIfVisible = (fieldName) =>
    (attendeeSchema || []).map(({ name }) => name).includes(fieldName);

  const checkIfRequired = (fieldName) =>
    !!attendeeSchema?.find(({ name }) => name === fieldName)?.required;

  const validators = getTravelDocumentsValidators({
    attendeeSchema,
    locale,
    arrivalDate,
    departureDate,
  });
  const travelDocumentLabelsMap = {
    idcard_document_number: t('booking.participants.labels.idcard.id'),
    idcard_expiration_date: t('booking.participants.labels.idcard.expirationDate'),
    idcard_issuing_date: t('booking.participants.labels.idcard.issuingDate'),
    idcard_authority: t('booking.participants.labels.idcard.issuingPlace'),
    idcard_birth_country: t('booking.participants.labels.idcard.birthCountry'),
    idcard_birth_city: t('booking.participants.labels.idcard.birthCity'),
    idcard_second_name: t('booking.participants.labels.idcard.secondName'),
    idcard_third_name: t('booking.participants.labels.idcard.thirdName'),
    idcard_fourth_name: t('booking.participants.labels.idcard.fourthName'),
    idcard_nationality: t('booking.participants.labels.idcard.nationality'),
    passport_document_number: t('booking.participants.labels.passport.id'),
    passport_expiration_date: t('booking.participants.labels.passport.expirationDate'),
    passport_issuing_date: t('booking.participants.labels.passport.issuingDate'),
    passport_authority: t('booking.participants.labels.passport.issuingPlace'),
    passport_birth_country: t('booking.participants.labels.passport.birthCountry'),
    passport_birth_city: t('booking.participants.labels.passport.birthCity'),
    passport_second_name: t('booking.participants.labels.passport.secondName'),
    passport_third_name: t('booking.participants.labels.passport.thirdName'),
    passport_fourth_name: t('booking.participants.labels.passport.fourthName'),
    passport_nationality: t('booking.participants.labels.passport.nationality'),
    identity_number: t('booking.participants.labels.identityNumber'),
  };

  const getTypeOfDocument = (document) => {
    return document.split('_')[0];
  };

  const documentFieldsObject = Object.keys(travelDocumentLabelsMap).reduce((acc, val) => {
    return {
      ...acc,
      [camelCase(val)]: {
        isVisible: checkIfVisible(val),
        isRequired: checkIfRequired(val),
        label: travelDocumentLabelsMap[val],
        type: getTypeOfDocument(val),
        validator: validators[camelCase(val)],
      },
    };
  }, {});
  return {
    civilityField: {
      isVisible: checkIfVisible('civility'),
      isRequired: checkIfRequired('civility'),
    },
    firstNameField: {
      isVisible: checkIfVisible('first_name'),
      isRequired: checkIfRequired('first_name'),
    },
    lastNameField: {
      isVisible: checkIfVisible('last_name'),
      isRequired: checkIfRequired('last_name'),
    },
    unicodeFirstNameField: {
      isVisible: checkIfVisible('unicode_first_name'),
      isRequired: checkIfRequired('unicode_first_name'),
    },
    unicodeLastNameField: {
      isVisible: checkIfVisible('unicode_last_name'),
      isRequired: checkIfRequired('unicode_last_name'),
    },
    emailField: {
      isVisible: checkIfVisible('email'),
      isRequired: checkIfRequired('email'),
    },
    birthdateField: {
      isVisible: checkIfVisible('birthdate'),
      isRequired: checkIfRequired('birthdate'),
    },
    gmNumberField: {
      isVisible: checkIfVisible('gm_number'),
      isRequired: checkIfRequired('gm_number'),
    },
    phonesField: {
      isVisible: checkIfVisible('phones'),
      isRequired: checkIfRequired('phones'),
    },
    sanitaryIdField: {
      isVisible: checkIfVisible('health_pass_id'),
      isRequired: checkIfRequired('health_pass_id'),
    },
    documentFieldsVisibilities: documentFieldsObject,
    shouldDisplayLegalInfo: !!Object.values(documentFieldsObject).find(
      ({ isVisible }) => isVisible,
    ),
  };
};
export const isSchemaHasUnicodeFields = (attendeeSchema: Array<ValidationSchema>) => {
  const attendeeSchemaFields = (attendeeSchema || []).map(({ name }) => name);
  return (
    attendeeSchemaFields.includes('unicode_first_name') &&
    attendeeSchemaFields.includes('unicode_last_name')
  );
};
